/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import {
    getAddressPurposesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { AddressPurpose } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';

// @CUSTOM: new import for filtering purpose by config settings
import { AddressTypesToDisplay } from '../settings/app.settings';

/**
 * Input class for get Address Purposes data action.
 */
export class GetAddressPurposesInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('AddressPurpose', this.apiSettings);

    public getCacheObjectType = () => 'AddressPurpose';

    public dataCacheType = (): CacheType => 'application';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetAddressPurposesInput => {
    return new GetAddressPurposesInput(inputData.requestContext.apiSettings);
};

export async function getAddressPurposesAction(input: GetAddressPurposesInput, ctx: IActionContext): Promise<AddressPurpose[]> {

    // @CUSTOM: adds filter to address types from global config
    // instead of returning next line, customization applied to filter first
    const purposes = await getAddressPurposesAsync({ callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) });
    const includeList: AddressTypesToDisplay = ctx.requestContext.app.config.addressTypesToDisplay;

    if (includeList.length) {

        const allowedPurposes: AddressPurpose[] = [];

        purposes.forEach(purpose => {

            const allowed = includeList?.find(allowedPurpose => {
                return allowedPurpose === purpose.Name;
            });

            if (allowed) {
                allowedPurposes.push(purpose);
            }

        });

        return allowedPurposes;

    }

    // fallback if no filter set in global config
    return purposes;
    // Customization end

}

export const getAddressPurposesActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/address/get-address-purposes',
    action: <IAction<AddressPurpose[]>>getAddressPurposesAction,
    input: createInput
});

export default getAddressPurposesActionDataAction;
